import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import {
  ELIGIBILITY_CHECK_STATUS,
  ELIGIBILITY_MATCH_TIER,
  ELIGIBILITY_OUTCOME,
  ELIGIBILITY_RESOLUTION_PROVIDER,
  ELIGIBILITY_VIEW_STATUS,
  eligibilityCheckStatusSchema,
  eligibilityMatchTierSchema,
  eligibilityOutcomeSchema,
  eligibilityResolutionProviderSchema,
  eligibilityViewStatusSchema,
} from '../eligibility/schema';
import {
  ELIGIBILITY_REVIEW_REASON_UNRECOGNISED,
  ELIGIBILITY_STAFF_REVIEW_REASON,
} from '../eligibility/review-reason';
import { ELIGIBILITY_RELATIONSHIP } from '../programs/schema';

/**
 * A staff read of one member's eligibility: the derived verification status,
 * the check history as lifecycle facts, the sponsor, the plan identifiers the
 * member gave at signup, and their current eligible grants.
 *
 * Minimum necessary by design. A check carries NO submitted identity (name,
 * birth date, ZIP, access code, email) and none of the matched roster record
 * (`profileCandidate`, `matchedMemberRef`, `hdhpFlag`): the member's verified
 * identity is already on the member read, and this surface adds only what
 * verification needs on top of it. The plan member ID and group number are
 * PHI; they reach an authorised, audited staff caller and nothing else.
 *
 * Every field is required, nullable where the fact can be absent. The route is
 * new, so the only clients that can call it are ones built with it (ADR-135
 * Rule 4). Not `.strict()`: a response schema strips unknown keys instead.
 */

export const MEMBER_ELIGIBILITY_CHECKS_LIMIT = 50;

const staffReviewReasonSchema = z.enum(ELIGIBILITY_STAFF_REVIEW_REASON);

/** One tier's attempt inside a check: lifecycle facts only. */
export const memberEligibilityCascadeEntrySchema = z.object({
  provider: eligibilityResolutionProviderSchema,
  outcome: eligibilityOutcomeSchema,
  reviewReason: staffReviewReasonSchema.nullable(),
});

/**
 * The columns of an `eligibility_checks` row this view shows, as they are on the
 * table. Kept as its own object so the drift guard can tie it to the table.
 */
export const memberEligibilityCheckRowSchema = z.object({
  id: z.string().uuid(),
  status: eligibilityCheckStatusSchema,
  createdAt: z.string().datetime({ offset: true }),
  resolvedAt: z.string().datetime({ offset: true }).nullable(),
  clientRef: z.string().nullable(),
});

/** The safe parts of the check's stored resolution; all null when it has none. */
export const memberEligibilityCheckResolutionSchema = z.object({
  outcome: eligibilityOutcomeSchema.nullable(),
  provider: eligibilityResolutionProviderSchema.nullable(),
  matchTier: eligibilityMatchTierSchema.nullable(),
  confidenceScore: z.number().min(0).max(1).nullable(),
  reviewReason: staffReviewReasonSchema.nullable(),
  cascade: z.array(memberEligibilityCascadeEntrySchema),
});

export const memberEligibilityCheckSchema = memberEligibilityCheckRowSchema.merge(
  memberEligibilityCheckResolutionSchema,
);

/** The columns of a `member_eligibilities` row this view shows. */
export const memberEligibilityGrantRowSchema = z.object({
  clientRef: z.string().nullable(),
  subClientId: z.string().nullable(),
  relationship: z.enum(ELIGIBILITY_RELATIONSHIP),
  validFrom: z.string().datetime({ offset: true }).nullable(),
  validTo: z.string().datetime({ offset: true }).nullable(),
  createdAt: z.string().datetime({ offset: true }),
});

/** An eligible grant: the row's columns plus the program's catalog key. */
export const memberEligibilityGrantSchema = memberEligibilityGrantRowSchema.extend({
  programKey: z.string(),
});

/** The plan identifiers from `member_profiles`; both null when not given. */
export const memberEligibilityPlanDetailsSchema = z.object({
  planMemberId: z.string().nullable(),
  planGroupNo: z.string().nullable(),
});

export const memberEligibilitySponsorSchema = z.object({
  clientRef: z.string(),
  /** The company-config display name, or the ref itself when no name resolves. */
  name: z.string(),
  subClientId: z.string().nullable(),
});

export const memberEligibilityDataSchema = z.object({
  status: eligibilityViewStatusSchema,
  latestCheck: memberEligibilityCheckSchema.nullable(),
  checks: z.array(memberEligibilityCheckSchema),
  checksTruncated: z.boolean(),
  sponsor: memberEligibilitySponsorSchema.nullable(),
  planDetails: memberEligibilityPlanDetailsSchema,
  grants: z.array(memberEligibilityGrantSchema),
});

export const memberEligibilityResponseSchema = apiEnvelope(memberEligibilityDataSchema);

export type MemberEligibilityCascadeEntry = z.infer<typeof memberEligibilityCascadeEntrySchema>;
export type MemberEligibilityCheck = z.infer<typeof memberEligibilityCheckSchema>;
export type MemberEligibilityGrant = z.infer<typeof memberEligibilityGrantSchema>;
export type MemberEligibilityPlanDetails = z.infer<typeof memberEligibilityPlanDetailsSchema>;
export type MemberEligibilitySponsor = z.infer<typeof memberEligibilitySponsorSchema>;
export type MemberEligibilityData = z.infer<typeof memberEligibilityDataSchema>;
export type MemberEligibilityResponse = z.infer<typeof memberEligibilityResponseSchema>;

// ─── client (Atlas) schemas ─────────────────────────────────────────────────

/** What a client shows for an enum value this build has not heard of. */
export const MEMBER_ELIGIBILITY_UNKNOWN = 'unknown';

/**
 * A string that resolves to one of `values`, or to `fallback` when it is a
 * string outside them. The leading `z.string()` keeps the tolerance narrow: a
 * missing field, a null or a number is a malformed payload, not a newer
 * vocabulary, and still fails the parse (the emergency-details client schema's
 * reasoning).
 */
export function tolerantEnum<const V extends readonly string[], const F extends string>(
  values: V,
  fallback: F,
) {
  const known = (value: string): value is V[number] => values.some((v) => v === value);
  return z.string().transform((value): V[number] | F => (known(value) ? value : fallback));
}

const clientReviewReasonSchema = tolerantEnum(
  ELIGIBILITY_STAFF_REVIEW_REASON,
  ELIGIBILITY_REVIEW_REASON_UNRECOGNISED,
);
const clientProviderSchema = tolerantEnum(
  ELIGIBILITY_RESOLUTION_PROVIDER,
  MEMBER_ELIGIBILITY_UNKNOWN,
);
const clientOutcomeSchema = tolerantEnum(ELIGIBILITY_OUTCOME, MEMBER_ELIGIBILITY_UNKNOWN);

const memberEligibilityClientCheckSchema = memberEligibilityCheckSchema.extend({
  status: tolerantEnum(ELIGIBILITY_CHECK_STATUS, MEMBER_ELIGIBILITY_UNKNOWN),
  outcome: clientOutcomeSchema.nullable(),
  provider: clientProviderSchema.nullable(),
  matchTier: tolerantEnum(ELIGIBILITY_MATCH_TIER, MEMBER_ELIGIBILITY_UNKNOWN).nullable(),
  reviewReason: clientReviewReasonSchema.nullable(),
  cascade: z.array(
    memberEligibilityCascadeEntrySchema.extend({
      provider: clientProviderSchema,
      outcome: clientOutcomeSchema,
      reviewReason: clientReviewReasonSchema.nullable(),
    }),
  ),
});

/**
 * What Atlas parses. The same shape as the wire schema, except that each
 * enum-valued field resolves an unrecognised string to `unknown` (or
 * `unrecognised` for a reason) instead of throwing, so a server that learns a
 * new status does not blank the card in an older bundle.
 */
export const memberEligibilityClientDataSchema = memberEligibilityDataSchema.extend({
  status: tolerantEnum(ELIGIBILITY_VIEW_STATUS, MEMBER_ELIGIBILITY_UNKNOWN),
  latestCheck: memberEligibilityClientCheckSchema.nullable(),
  checks: z.array(memberEligibilityClientCheckSchema),
  grants: z.array(
    memberEligibilityGrantSchema.extend({
      relationship: tolerantEnum(ELIGIBILITY_RELATIONSHIP, MEMBER_ELIGIBILITY_UNKNOWN),
    }),
  ),
});

export const memberEligibilityClientResponseSchema = apiEnvelope(memberEligibilityClientDataSchema);

export type MemberEligibilityClientData = z.infer<typeof memberEligibilityClientDataSchema>;
